import { Calendar as CalendarIcon, Users, Clock } from "lucide-react";

/**
 * Illustrative UI mocks for the Boat Club marketing page.
 * These are stylised mockups, NOT real product screenshots. They demonstrate
 * capability (reservation limits, peak days, balances, household captains).
 * Dusty-blue brand, light theme, Space Grotesk display, Inter body — matches
 * the rest of the public site.
 */

/* ------------------------------------------------------------------ */
/* Reservation meter — "3 of 4 reservations used"                     */
/* ------------------------------------------------------------------ */
export function ReservationMeter({ used = 3, total = 4, peakUsed = 2, peakTotal = 2 }) {
  const pct = Math.min(100, Math.round((used / total) * 100));
  return (
    <div
      className="rounded-2xl border border-ra-line bg-white p-5 shadow-card"
      data-testid="bc-reservation-meter"
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ra-ink/55">
          Active reservations
        </p>
        <span className="rounded-full bg-ra-ocean/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ra-oceanDark">
          Member
        </span>
      </div>
      <p className="mt-3 font-display text-3xl font-semibold text-ra-ink leading-none">
        {used}
        <span className="text-ra-ink/40"> / {total}</span>
      </p>
      <p className="mt-1 text-xs text-ra-ink/60">Future bookings held</p>
      <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-ra-paper">
        <div
          className="h-full rounded-full bg-ra-ocean transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-ra-line/70 pt-3 text-xs">
        <span className="text-ra-ink/60">Peak (weekend / holiday)</span>
        <span className="font-semibold text-ra-ink">
          {peakUsed}
          <span className="text-ra-ink/40"> / {peakTotal}</span>
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Balance chip — "21 HRS remaining"                                  */
/* ------------------------------------------------------------------ */
export function BalanceChip({ value = 21, unit = "HRS", label = "Remaining this season" }) {
  return (
    <div
      className="inline-flex items-center gap-3 rounded-2xl border border-ra-line bg-white px-5 py-3 shadow-card"
      data-testid="bc-balance-chip"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ra-amber/15 text-ra-amber">
        <Clock size={16} />
      </span>
      <div className="leading-tight">
        <p className="font-display text-2xl font-semibold text-ra-ink">
          {value}
          <span className="ml-1 text-sm font-semibold tracking-wider text-ra-ink/55">{unit}</span>
        </p>
        <p className="text-[11px] text-ra-ink/60">{label}</p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Peak calendar — one week, with weekend + holiday cells accented    */
/* ------------------------------------------------------------------ */
const WEEK_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

export function PeakCalendar() {
  // 0 = normal, 1 = peak, 2 = holiday (also peak)
  const row1 = [0, 0, 0, 0, 2, 1, 1];
  const row2 = [0, 0, 0, 0, 0, 1, 1];
  const rows = [row1, row2];

  const cellCls = (v) => {
    if (v === 2) return "bg-ra-coral/15 text-ra-coral border-ra-coral/30"; // holiday
    if (v === 1) return "bg-ra-ocean/15 text-ra-oceanDark border-ra-ocean/30"; // peak
    return "bg-white text-ra-ink/55 border-ra-line";
  };

  return (
    <div
      className="rounded-2xl border border-ra-line bg-white p-5 shadow-card"
      data-testid="bc-peak-calendar"
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ra-ink/55">
          July · Peak schedule
        </p>
        <CalendarIcon size={14} className="text-ra-ink/40" />
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1.5">
        {WEEK_LABELS.map((d, i) => (
          <div
            key={`h-${i}`}
            className="text-center text-[10px] font-semibold uppercase tracking-wider text-ra-ink/50"
          >
            {d}
          </div>
        ))}
        {rows.flatMap((row, ri) =>
          row.map((v, ci) => (
            <div
              key={`c-${ri}-${ci}`}
              className={`aspect-square flex items-center justify-center rounded-lg border text-[11px] font-semibold ${cellCls(v)}`}
            >
              {ri * 7 + ci + 1}
            </div>
          ))
        )}
      </div>
      <div className="mt-4 flex items-center gap-3 text-[10px] text-ra-ink/60">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-ra-ocean/40" /> Peak
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-ra-coral/40" /> Holiday
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Household card — primary + additional captain                      */
/* ------------------------------------------------------------------ */
export function HouseholdCard() {
  const members = [
    { initials: "SK", name: "Sarah K.", role: "Primary", can: true },
    { initials: "DK", name: "Derek K.", role: "Captain", can: true },
  ];
  return (
    <div
      className="rounded-2xl border border-ra-line bg-white p-5 shadow-card"
      data-testid="bc-household-card"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ra-ink/55">
            Household membership
          </p>
          <p className="mt-1 font-display text-lg font-semibold text-ra-ink">All-Access · Karras</p>
        </div>
        <Users size={16} className="text-ra-ink/40" />
      </div>
      <ul className="mt-4 space-y-2.5">
        {members.map((m) => (
          <li key={m.initials} className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ra-ocean/15 text-xs font-semibold text-ra-oceanDark">
              {m.initials}
            </span>
            <div className="flex-1 leading-tight">
              <p className="text-sm font-semibold text-ra-ink">{m.name}</p>
              <p className="text-[11px] text-ra-ink/55">{m.role}</p>
            </div>
            {m.can && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-700">
                Can reserve
              </span>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-4 border-t border-ra-line/70 pt-3 text-[11px] text-ra-ink/60">
        Shared limits: both captains draw from the same 4-reservation plan.
      </p>
    </div>
  );
}
